
import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle, AlertCircle, RotateCcw, Copy } from "lucide-react";

export default function EmailTemplateSettings() {
  const [message, setMessage] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [kioskPin, setKioskPin] = useState("");
  const queryClient = useQueryClient();

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const kioskSettings = settings.find((s) => s.setting_key === "kiosk_pin");
  const maintenanceSettings = settings.find((s) => s.setting_key === "maintenance_mode");
  const maintenanceEnabled = maintenanceSettings?.enabled === true;

  useEffect(() => {
    if (kioskSettings) {
      setKioskPin(kioskSettings.pin || "");
    }
  }, [kioskSettings]);

  const saveKioskPinMutation = useMutation({
    mutationFn: async () => {
      const pinValue = String(kioskPin || "").trim();
      if (!/^\d{4}$/.test(pinValue)) {
        throw new Error("PIN must be exactly 4 digits.");
      }
      if (kioskSettings?.id) {
        await base44.entities.Settings.update(kioskSettings.id, {
          pin: pinValue,
          setting_key: "kiosk_pin",
        });
      } else {
        await base44.entities.Settings.create({
          setting_key: "kiosk_pin",
          pin: pinValue,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["settings"]);
      setMessage({ type: "success", text: "Kiosk PIN saved." });
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (e) => {
      setMessage({ type: "error", text: e.message || "Failed to save PIN" });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  const toggleMaintenanceMutation = useMutation({
    mutationFn: async () => {
      const nextEnabled = !maintenanceEnabled;
      if (maintenanceSettings?.id) {
        await base44.entities.Settings.update(maintenanceSettings.id, {
          enabled: nextEnabled,
          setting_key: "maintenance_mode",
        });
      } else {
        await base44.entities.Settings.create({
          setting_key: "maintenance_mode",
          enabled: nextEnabled,
        });
      }
      return nextEnabled;
    },
    onSuccess: (nextEnabled) => {
      queryClient.invalidateQueries(["settings"]);
      setMessage({
        type: "success",
        text: nextEnabled
          ? "Maintenance mode is ON. Non-admin users now see the maintenance screen."
          : "Maintenance mode is OFF. Normal access restored.",
      });
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (e) => {
      setMessage({ type: "error", text: e.message || "Failed to toggle maintenance mode" });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  const emailSettings = settings.find(s => s.setting_key === 'email_template');

  useEffect(() => {
    const defaultSubject = '⚠️ EA Consequences - Students Requiring Intervention';
    const defaultBody = `Dear {ap_name},

You have {student_count} student{student_plural} in your grade cohort who require intervention for refusing to attend Eagle Academy.

STUDENTS REQUIRING ATTENTION:
{student_list}

NEXT STEPS:
1. Log into the EA Management System
2. Navigate to "EA Consequences" in the menu
3. Review each student's refusal history
4. Document interventions taken
5. Track makeup day completion
6. Update parent contact information as needed

Please address these cases as soon as possible. Each refusal day requires a makeup day, and consequences escalate based on the number of refusals.

If you have questions or need support, please contact the EA Coordinator.

Thank you for your prompt attention to these matters.

Eagle Academy Management System
This is an automated notification.`;

    if (emailSettings) {
      setSubject(emailSettings.email_subject || defaultSubject);
      setBody(emailSettings.email_body || defaultBody);
    } else if (!isLoading) {
      // Set defaults if no settings exist yet and loading is complete
      setSubject(defaultSubject);
      setBody(defaultBody);
    }
  }, [settings, isLoading]);

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const existing = settings.find(s => s.setting_key === 'email_template');
      
      if (existing) {
        await base44.entities.Settings.update(existing.id, {
          email_subject: subject,
          email_body: body,
          setting_key: 'email_template'
        });
      } else {
        await base44.entities.Settings.create({
          email_subject: subject,
          email_body: body,
          setting_key: 'email_template'
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      setMessage({ type: 'success', text: 'Email template saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: error.message || 'Failed to save template' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const resetToDefaultMutation = useMutation({
    mutationFn: async () => {
      const existing = settings.find(s => s.setting_key === 'email_template');
      
      const defaultSubject = '⚠️ EA Consequences - Students Requiring Intervention';
      const defaultBody = `Dear {ap_name},

You have {student_count} student{student_plural} in your grade cohort who require intervention for refusing to attend Eagle Academy.

STUDENTS REQUIRING ATTENTION:
{student_list}

NEXT STEPS:
1. Log into the EA Management System
2. Navigate to "EA Consequences" in the menu
3. Review each student's refusal history
4. Document interventions taken
5. Track makeup day completion
6. Update parent contact information as needed

Please address these cases as soon as possible. Each refusal day requires a makeup day, and consequences escalate based on the number of refusals.

If you have questions or need support, please contact the EA Coordinator.

Thank you for your prompt attention to these matters.

Eagle Academy Management System
This is an automated notification.`;

      if (existing) {
        await base44.entities.Settings.update(existing.id, {
          email_subject: defaultSubject,
          email_body: defaultBody,
          setting_key: 'email_template'
        });
      } else {
        await base44.entities.Settings.create({
          email_subject: defaultSubject,
          email_body: defaultBody,
          setting_key: 'email_template'
        });
      }

      setSubject(defaultSubject);
      setBody(defaultBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      setMessage({ type: 'success', text: 'Reset to default template!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: error.message || 'Failed to reset template' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const fieldInserts = [
    { key: '{ap_name}', description: 'AP\'s full name' },
    { key: '{student_count}', description: 'Number of students (e.g., "3")' },
    { key: '{student_plural}', description: 'Automatically adds "s" if count > 1' },
    { key: '{student_list}', description: 'Formatted list of students with details' },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: `Copied "${text}" to clipboard!` });
    setTimeout(() => setMessage(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Email Template</h1>
              <p className="text-slate-600 mt-1">Customize AP Notification Emails</p>
            </div>
          </div>
          <Button 
            onClick={() => resetToDefaultMutation.mutate()}
            variant="outline"
            className="border-slate-300"
            disabled={resetToDefaultMutation.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {resetToDefaultMutation.isPending ? 'Resetting...' : 'Reset to Default'}
          </Button>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-900">Available Field Inserts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-blue-800 mb-4">
              Click on any field insert to copy it. Use these placeholders in your email template - they'll be replaced with actual data when emails are sent.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fieldInserts.map((field) => (
                <div 
                  key={field.key}
                  onClick={() => copyToClipboard(field.key)}
                  className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 cursor-pointer transition-colors"
                >
                  <Copy className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-blue-600 text-white font-mono text-xs mb-1">
                      {field.key}
                    </Badge>
                    <p className="text-sm text-slate-700">{field.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <Alert className="mt-4 bg-yellow-50 border-yellow-200">
              <AlertCircle className="w-4 h-4 text-yellow-700" />
              <AlertDescription className="text-yellow-800 text-sm">
                <strong>Note:</strong> {'{student_list}'} automatically formats each student as: "• Name (Grade X) - X refusal days - Consequence Level - X makeup days remaining - Last Refusal: Date"
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Email Subject</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line..."
              className="text-lg"
            />
            <p className="text-xs text-slate-500 mt-2">
              You can use field inserts like {'{ap_name}'} or {'{student_count}'} in the subject
            </p>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Email Body</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body content..."
              className="h-96 font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-2">
              Use field inserts like {'{ap_name}'}, {'{student_count}'}, {'{student_plural}'}, and {'{student_list}'} in your template
            </p>
          </CardContent>
        </Card>

        <Button 
          onClick={() => saveTemplateMutation.mutate()}
          className="w-full bg-blue-700 hover:bg-blue-800"
          disabled={saveTemplateMutation.isPending}
        >
          {saveTemplateMutation.isPending ? (
            <>Saving...</>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Email Template
            </>
          )}
        </Button>

        <Card className="border-none shadow-lg bg-slate-50">
          <CardHeader>
            <CardTitle className="text-sm">Preview Example</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-4">
            <div>
              <p className="font-semibold text-slate-700">Subject:</p>
              <p className="text-slate-900 mt-1">
                {subject.replace('{ap_name}', 'John Smith').replace('{student_count}', '3').replace('{student_plural}', 's')}
              </p>
            </div>
            <div>
              <p className="font-semibold text-slate-700">Body Preview (first 200 characters):</p>
              <p className="text-slate-900 mt-1 whitespace-pre-wrap">
                {body
                  .replace('{ap_name}', 'John Smith')
                  .replace('{student_count}', '3')
                  .replace('{student_plural}', 's')
                  .replace('{student_list}', '• Jane Doe (Grade 5) - 3 refusal days - Consequence: Detention - 2 makeup days remaining - Last Refusal: Jan 15, 2025\n• John Smith (Grade 6) - 2 refusal days - Consequence: Parent Contact - 1 makeup day remaining - Last Refusal: Jan 14, 2025')
                  .substring(0, 200)}...
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Kiosk PIN settings — 4-digit gate for the /kiosk route */}
        <Card className="border-none shadow-lg" data-testid="kiosk-pin-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Kiosk PIN
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              This 4-digit PIN gates the public <code className="px-1 rounded bg-slate-100">/kiosk</code> route.
              Change it whenever you rotate staff or suspect it's been shared with students.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="kiosk-pin">PIN (4 digits)</Label>
                <Input
                  id="kiosk-pin"
                  value={kioskPin}
                  onChange={(e) => setKioskPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="1234"
                  maxLength={4}
                  className="h-12 text-xl font-mono tracking-widest text-center"
                  data-testid="kiosk-pin-settings-input"
                />
              </div>
              <Button
                onClick={() => saveKioskPinMutation.mutate()}
                disabled={saveKioskPinMutation.isPending || kioskPin.length !== 4}
                className="bg-slate-800 hover:bg-slate-900 h-12"
                data-testid="kiosk-pin-save"
              >
                {saveKioskPinMutation.isPending ? "Saving..." : "Save PIN"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Mode toggle — locks out every non-admin user. */}
        <Card className="border-none shadow-lg" data-testid="maintenance-mode-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Maintenance Mode
              <Badge
                data-testid="maintenance-mode-state"
                className={
                  maintenanceEnabled
                    ? "bg-red-100 text-red-800"
                    : "bg-green-100 text-green-800"
                }
              >
                {maintenanceEnabled ? "ON" : "OFF"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              When ON, every logged-in user who is <strong>not</strong> an admin sees a full-screen
              "This app is currently unavailable" message. Admins keep full access. The state is
              stored in the database so it survives restarts.
            </p>
            <Button
              onClick={() => {
                const msg = maintenanceEnabled
                  ? "Turn maintenance mode OFF? Non-admin users will regain access immediately."
                  : "Turn maintenance mode ON? Every non-admin user will be blocked immediately.";
                if (window.confirm(msg)) toggleMaintenanceMutation.mutate();
              }}
              disabled={toggleMaintenanceMutation.isPending}
              className={
                maintenanceEnabled
                  ? "bg-green-600 hover:bg-green-700 h-12"
                  : "bg-red-600 hover:bg-red-700 h-12"
              }
              data-testid="maintenance-mode-toggle"
            >
              {toggleMaintenanceMutation.isPending
                ? "Updating..."
                : maintenanceEnabled
                ? "Turn Maintenance Mode OFF"
                : "Turn Maintenance Mode ON"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
